import { OfferCardProps } from 'app/components/OfferCard';
import { ImagePropsFragment, Locale } from 'app/generated/hygraph';
import { ProductContent } from 'app/hooks/useProduct';
import {
  getOffers as getAttractionOffers,
  getSharedAdmissionOffers,
} from 'app/services/AttractionHelper';
import { TranslationHelper } from 'app/services/I18n';
import { webHome } from 'app/services/LinkHelper';
import { cdn, ImageTransform } from 'app/utils/image';

type Image = {
  url: string;
  width: number;
  height: number;
  ratio: number;
};

export function getUrl(product: Maybe<ProductContent>, locale: Locale = Locale.En): string {
  return `${webHome({ locale })}/${product?.slug}`;
}

export function getCityName(product: Maybe<ProductContent>): string {
  return product?.cityName?.value ?? '';
}

export function getHero(product: Maybe<ProductContent>, transforms?: ImageTransform[]): Image {
  return getImage(product, 'hero', transforms);
}

export function getLogo(product: Maybe<ProductContent>): Image {
  return getImage(product, 'logo', [['output', { format: 'png', density: 200 }]]);
}

export function getLogoWhite(product: Maybe<ProductContent>): Image {
  return getImage(product, 'logoWhite', [['output', { format: 'png', density: 200 }]]);
}

export function getTicketsBackground(product: Maybe<ProductContent>): Maybe<ImagePropsFragment> {
  return product?.reservation?.ticketsBackground ?? product?.hero ?? null;
}

export function hasFAQs(product: Maybe<ProductContent>) {
  return product?.faqs?.length !== 0;
}

export function hasBroadway(product: Maybe<ProductContent>) {
  return /^new-york/.test(product?.key ?? '');
}

export function getOffers(product: Maybe<ProductContent>) {
  return product?.attractions.flatMap((attr) => getAttractionOffers(attr)) ?? [];
}

export function hasOffers(product: Maybe<ProductContent>) {
  return getOffers(product).length > 0;
}

export function getOptionTicketAdmissionOffers(product: Maybe<ProductContent>) {
  return (
    product?.attractions.flatMap((attr) =>
      getSharedAdmissionOffers(attr).map((offer) => ({
        ...offer,
        $attraction: attr,
      }))
    ) ?? []
  );
}

type OfferDisplayGroup = {
  id: string;
  attractionContentKey: string;
  title: string;
  offers: OfferCardProps['offer'][];
  theme?: OfferCardProps['theme'];
};

export function getOfferDisplayGroups(
  product: Maybe<ProductContent>,
  t: TranslationHelper
): OfferDisplayGroup[] {
  const groups = [];

  const optionOffers = getOptionTicketAdmissionOffers(product);
  if (optionOffers.length > 0) {
    const optionGroup: OfferDisplayGroup = {
      id: 'optionTickets',
      attractionContentKey: '',
      title: t('app_product_offers_choice_title'),
      offers: optionOffers.map((offer) => ({
        ...offer,
        title: offer.$attraction.name ?? '',
        subtitle: offer.title ?? '',
      })),
      theme: 'highlight',
    };
    groups.push(optionGroup);
  }

  product?.attractionLineups?.forEach((lineup) => {
    lineup.attractionGroups.forEach((group) => {
      group.attractions.forEach((attr) => {
        const attraction = (product?.attractions ?? []).find((a) => a.key === attr.key);
        const offers = getAttractionOffers(attraction);
        if (offers.length > 0 && attraction) {
          groups.push({
            id: attraction.key ?? '',
            attractionContentKey: attraction.key ?? '',
            title: attraction.name ?? '',
            offers,
          });
        }
      });
    });
  });

  return groups;
}

function getImage(
  product: Maybe<ProductContent>,
  prop: 'hero' | 'logo' | 'logoWhite' | 'logoTicket',
  transforms: ImageTransform[] = []
): Image {
  let width = product?.[prop]?.width || 0;
  let height = product?.[prop]?.height || 0;
  let ratio = width / height;

  if (Number.isNaN(ratio)) {
    ratio = 1;
  }

  // hygraph started returning 0 for width and height for some svg images
  if (!width || !height) {
    if (/^logo$/.test(prop)) {
      width = 360;
      height = 187;
      ratio = width / height;
    }
  }

  let variantUrl: string | undefined;

  if (prop === 'hero') {
    const variant = product?.[prop]?.variants?.find((v) => v.size === 'sm');
    variantUrl = variant?.asset.url;

    if (!variantUrl) {
      transforms.push(['resize', { fit: 'crop', width, height }]);
    }
  }

  const url = cdn(variantUrl || product?.[prop]?.url || '', transforms);

  return { url, width, height, ratio };
}
